import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from '../../lib/motion.js';
import { useUi } from '../../stores/ui.js';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * The add-to-cart flight (PRD §8).
 *
 * A clone of the product image arcs from where it was clicked to the bag icon.
 * It is purely decorative — `aria-hidden`, pointer-transparent, and skipped
 * entirely under reduced motion, where the toast and the badge do the
 * communicating.
 *
 * Two things it must not do, both of which it used to. It must land at fully
 * zero opacity, not a faint ghost, and it must be removed the instant the
 * animation ends rather than on a timer that outlives it — either one leaves a
 * translucent square hanging over the page. And it renders nothing at all
 * without an image, because an empty bordered box flying across the screen reads
 * as a glitch rather than an effect.
 */

const portalTarget = () => (typeof document === 'undefined' ? null : document.body);

export const FlyToCart = () => {
  const flyFrom = useUi((state) => state.flyFrom);
  const clearFlyFrom = useUi((state) => state.clearFlyFrom);
  const reduced = useReducedMotion();

  const [flight, setFlight] = useState(null);
  const target = portalTarget();

  useEffect(() => {
    if (!flyFrom) return;

    // Reduced motion, no image to fly, or no bag icon on screen: nothing to do.
    if (reduced || !target || !flyFrom.image) {
      clearFlyFrom();
      return;
    }

    const cartIcon = document.querySelector('[data-cart-target]');
    if (!cartIcon) {
      clearFlyFrom();
      return;
    }

    const destination = cartIcon.getBoundingClientRect();

    setFlight({
      id: Date.now(),
      image: flyFrom.image,
      from: { top: flyFrom.top, left: flyFrom.left, width: flyFrom.width, height: flyFrom.height },
      to: {
        x: destination.left + destination.width / 2 - (flyFrom.left + flyFrom.width / 2),
        y: destination.top + destination.height / 2 - (flyFrom.top + flyFrom.height / 2),
      },
    });

    clearFlyFrom();
  }, [flyFrom, reduced, clearFlyFrom, target]);

  if (!target) return null;

  return createPortal(
    <AnimatePresence>
      {flight && (
        <m.img
          key={flight.id}
          src={flight.image}
          alt=""
          aria-hidden="true"
          initial={{ x: 0, y: 0, opacity: 1, scale: 1, borderRadius: 0 }}
          animate={{
            x: [0, flight.to.x * 0.55, flight.to.x],
            y: [0, flight.to.y * 0.35 - 90, flight.to.y],
            scale: [1, 0.5, 0.08],
            // Ends at exactly zero. A ghost at 0.2 is what looked broken.
            opacity: [1, 0.9, 0],
            borderRadius: ['0%', '30%', '50%'],
          }}
          transition={{
            duration: 0.6,
            ease: [0.32, 0.72, 0.35, 1],
            times: [0, 0.5, 1],
            // Opacity holds until the last third, so the clone is solid for most
            // of the arc and gone by the time it lands.
            opacity: { duration: 0.6, times: [0, 0.62, 1], ease: 'easeIn' },
          }}
          // Removed the frame the animation finishes, not on a timer that could
          // outlive it.
          onAnimationComplete={() => setFlight(null)}
          className="pointer-events-none fixed z-[70] object-cover"
          style={{
            top: flight.from.top,
            left: flight.from.left,
            width: flight.from.width,
            height: flight.from.height,
            willChange: 'transform, opacity',
          }}
        />
      )}
    </AnimatePresence>,
    target,
  );
};

/**
 * Called by whatever was clicked. Measures the element and parks the rectangle
 * for one frame; the component above picks it up and runs the arc.
 *
 * The element passed in should be the image itself or a wrapper containing one.
 */
export const launchFlight = (element) => {
  if (!element) return;

  const image = element.matches?.('img') ? element : element.querySelector('img');
  if (!image) return;

  // Measure the image rather than its wrapper, or the clone starts at the wrong
  // size and visibly jumps on the first frame.
  const rect = image.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  useUi.getState().setFlyFrom({
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    image: image.currentSrc || image.src || null,
  });
};

export default FlyToCart;
